/**
 * BIN01 send protocol steps 3.3–3.5.
 * Max 10 files. Max 100 MB in total. A pair such as 54 MB + 72 MB is refused,
 * not split. dll and docx are refused. Other unknown types are refused.
 *
 * ALLOWED must stay in step with OBJECT_CODE in api/src/medbin/edi.ts.
 * Accepting a type that has no EDI object code makes the builder throw instead
 * of refusing the file.
 */

export const MEDBIN_MAX_FILES = 10;
export const MEDBIN_MAX_TOTAL_BYTES = 100 * 1024 * 1024;

const ALLOWED = new Set([
  "pdf",
  "gif",
  "jpg",
  "jpeg",
  "png",
  "txt",
  "tif",
  "tiff",
  "rtf",
  "zip",
  "wav",
  "doc",
  "xls",
  "dcm",
  "scp",
  "bmp",
]);

const BLOCKED = new Set(["dll", "docx"]);

export const MEDBIN_FILE_ACCEPT =
  ".pdf,.gif,.jpg,.jpeg,.png,.txt,.tif,.tiff,.rtf,.zip,.wav,.doc,.xls,.dcm,.scp,.bmp";

export type MedbinLimitCode =
  | "blocked-type"
  | "unknown-type"
  | "file-too-large"
  | "too-many-files"
  | "total-too-large";

export type MedbinFileLimit =
  | { ok: true }
  | { ok: false; code: MedbinLimitCode };

export function medbinFileExtension(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  if (dot < 0 || dot === fileName.length - 1) return "";
  return fileName.slice(dot + 1).toLowerCase();
}

export function medbinLimitMessage(code: MedbinLimitCode): string {
  switch (code) {
    case "blocked-type":
      return "dll and docx cannot be attached.";
    case "unknown-type":
      return "This file type is not allowed.";
    case "file-too-large":
      return "A single file cannot be over 100 MB.";
    case "too-many-files":
      return "There cannot be more than 10 files.";
    case "total-too-large":
      return "The files are over 100 MB together.";
  }
}

export function checkMedbinFile(input: {
  fileName: string;
  byteLength: number;
  existingFileCount: number;
  existingTotalBytes: number;
}): MedbinFileLimit {
  const extension = medbinFileExtension(input.fileName);
  if (BLOCKED.has(extension)) return { ok: false, code: "blocked-type" };
  if (!ALLOWED.has(extension)) return { ok: false, code: "unknown-type" };
  if (input.byteLength > MEDBIN_MAX_TOTAL_BYTES) return { ok: false, code: "file-too-large" };
  if (input.existingFileCount >= MEDBIN_MAX_FILES) return { ok: false, code: "too-many-files" };
  if (input.existingTotalBytes + input.byteLength > MEDBIN_MAX_TOTAL_BYTES) {
    return { ok: false, code: "total-too-large" };
  }
  return { ok: true };
}
